// components/PrivateRoute.tsx
import React, { useRef } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import Cookies from "universal-cookie";

import { useIdleTimeout } from "../hooks/useIdleTimeout";
import { isAuthorised, refreshToken, signOut } from "../services/auth";
import { markAppSessionEstablished } from "../services/unauthorizedSession";
import authStorage from "../utils/authStorage";
import { userDetails, isMasterVendor } from "../store";
import { useAppDispatch } from "../app/hooks";
import { clearAuth, setGlobalUserData } from "../pages/remittance-beta/state/authSlice";

const cookies = new Cookies();

const PrivateRoute: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const setUserDetails = useRecoilState(userDetails)[1];
  const [masterVendor, setMasterVendor] = useRecoilState(isMasterVendor);
  const isUseEffectFired = useRef(false);

  const currentUser = authStorage.getSession().currentUser;

  const handleTimeout = async () => {
    try {
      if (currentUser) {
        const response = await signOut(currentUser);
        if (response?.data) {
          alert("Session timeout, please login again.");
          cookies.remove("XSRF-TOKEN");
          localStorage.clear();
          dispatch(clearAuth());
          setIsLoggedIn(false);
          setIsLoading(true);
          setMasterVendor(false);
          navigate("/auth/sign-in-new");
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const {} = useIdleTimeout({
    timeout: 1800_000,
    onTimeout: handleTimeout,
  });

  React.useEffect(() => {
    if (!authStorage.hasValidTokens() || !currentUser?.vendor_key || !currentUser?.idToken) {
      setIsLoggedIn(false);
      setIsLoading(false);
      return;
    }

    if (isUseEffectFired.current) return;
    isUseEffectFired.current = true;

    isAuthorised(currentUser)
      .then((data) => {
        markAppSessionEstablished();
        setIsLoggedIn(true);
        setIsLoading(false);
        setUserDetails(data?.data);
        const authPayload = {
          isMasterVendor: Boolean(data?.data?.isMasterVendor),
          lanMandatory: data?.data?.lanMandatory,
          lanLength: data?.data?.lanLength,
        };
        authStorage.setSession(authPayload);
        if (data?.data?.isMasterVendor) {
          setMasterVendor(true);
          dispatch(setGlobalUserData({ isMasterVendor: data?.data?.isMasterVendor }));
        }
        dispatch(
          setGlobalUserData({
            lanMandatory: data?.data?.lanMandatory,
            lanLength: data?.data?.lanLength,
          })
        );
      })
      .catch(() => {
        setIsLoggedIn(false);
        setIsLoading(true);
        setMasterVendor(false);
        navigate("/auth/sign-in-new");
      });
  }, [currentUser, dispatch, navigate, setMasterVendor, setUserDetails]);

  React.useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      const idToken = localStorage.getItem("userToken") || "";
      if (idToken) {
        const decodedToken: any = jwtDecode(idToken);
        const tokenExpiryTime = moment.unix(decodedToken.exp);
        const currentTime = moment();

        if (tokenExpiryTime.diff(currentTime, "minutes") <= 10) {
          refreshToken(currentUser)
            .then((response: any) => {
              if (response?.data) {
                localStorage.setItem("userToken", response.data.token);
                dispatch(setGlobalUserData({ userToken: response.data.token }));
              }
            })
            .catch((error) => console.error(error));
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const userKey = localStorage.getItem("userKey");
  const userToken = localStorage.getItem("userToken");

  if (!userKey || !userToken) {
    return <Navigate to="/auth/sign-in-new" replace />;
  }

  return isLoading ? null : isLoggedIn ? <Outlet /> : <Navigate to="/auth/sign-in-new" />;
};

export default PrivateRoute;
